import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import {
  Link2, Unlink, Loader2, AlertCircle, CheckCircle, RefreshCw, ArrowLeft
} from 'lucide-react';
import subscriptionService from '../../services/subscription';
import type { XeroStatus } from '../../types/subscription';
import { useAuth } from '../../context/AuthContext';
import { isOrgSuperAdmin } from '../../utils/auth';
import { useOAuthReturn, isOAuthReturn } from '../../hooks/useOAuthReturn';

function XeroAdmin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isSuperAdmin = isOrgSuperAdmin(user);

  const [status, setStatus] = useState<XeroStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard', { replace: true });
      return;
    }

    // Check for callback params. A return carrying a code is not an outcome
    // yet: it is completed below.
    const connected = searchParams.get('connected');
    const callbackError = searchParams.get('error');
    if (isOAuthReturn(searchParams)) {
      // Nothing to report until the sign-in is finished.
    } else if (connected === 'true') {
      setSuccess('Xero connected successfully!');
    } else if (callbackError) {
      setError(`Xero connection failed: ${callbackError.replace(/_/g, ' ')}`);
    }

    loadStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Xero sends the browser back with a code; the super admin signed in here
  // completes the sign-in (see useOAuthReturn).
  const completeSignIn = useCallback(async (code: string, state: string) => {
    const res = await subscriptionService.completeXeroSignIn(code, state);
    if (res.success) setStatus(res.data);
  }, []);
  const onSignedIn = useCallback(() => setSuccess('Xero connected successfully!'), []);
  const finishingSignIn = useOAuthReturn(completeSignIn, onSignedIn, setError, 'Xero was not connected. Please connect again from this page.');

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await subscriptionService.getXeroStatus();
      if (res.success) {
        setStatus(res.data);
      }
    } catch {
      setError('Failed to load Xero status');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setActionLoading('connect');
    setError(null);
    try {
      const res = await subscriptionService.getXeroAuthorizationUrl();
      if (res.success && res.data.authorization_url) {
        // Open Xero OAuth in the current window
        window.location.href = res.data.authorization_url;
      }
    } catch {
      setError('Failed to initiate Xero connection');
      setActionLoading(null);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Disconnect Xero? New invoices will not be generated until reconnected.')) return;
    setActionLoading('disconnect');
    setError(null);
    try {
      const res = await subscriptionService.disconnectXero();
      if (res.success) {
        setStatus({ connected: false, tenant_name: null, token_expires_at: null });
        setSuccess('Xero disconnected');
      }
    } catch {
      setError('Failed to disconnect Xero');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSyncStatuses = async () => {
    setActionLoading('sync');
    setError(null);
    try {
      const res = await subscriptionService.syncXeroStatuses();
      if (res.success) {
        setSuccess(res.message || 'Invoice statuses synced');
      }
    } catch {
      setError('Failed to sync invoice statuses');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Xero Integration"
          subtitle="Manage Xero billing connection"
          icon={<Link2 className="h-6 w-6 text-white" />}
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Xero Integration"
        subtitle="Manage Xero billing connection for automatic invoice generation"
        icon={<Link2 className="h-6 w-6 text-white" />}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4" />
            Back to Admin
          </Button>
        </div>

        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-xl p-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-error-600 flex-shrink-0" />
            <p className="text-error-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-success-50 border border-success-200 rounded-xl p-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-success-600 flex-shrink-0" />
            <p className="text-success-700">{success}</p>
          </div>
        )}

        {finishingSignIn && (
          <div className="mb-6 bg-neutral-50 border border-neutral-200 rounded-xl p-4 flex items-center gap-2">
            <Loader2 className="h-5 w-5 text-neutral-500 flex-shrink-0 animate-spin" />
            <p className="text-neutral-700">Finishing the Xero sign-in…</p>
          </div>
        )}

        {/* Connection Status */}
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <div className="p-6 border-b border-neutral-200">
            <h2 className="text-xl font-bold text-neutral-900">Connection Status</h2>
          </div>

          <div className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-4 h-4 rounded-full ${status?.connected ? 'bg-success-500' : 'bg-error-500'}`} />
              <div>
                <div className="font-semibold text-neutral-900">
                  {status?.connected ? 'Connected' : 'Not Connected'}
                </div>
                {status?.tenant_name && (
                  <div className="text-sm text-neutral-500">
                    Organisation: {status.tenant_name}
                  </div>
                )}
              </div>
            </div>

            {status?.connected ? (
              <div className="space-y-4">
                <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                  <p className="text-sm text-success-700">
                    Xero is connected. Invoices will be automatically generated when org admins purchase or upgrade subscriptions.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleSyncStatuses}
                    disabled={actionLoading === 'sync'}
                  >
                    {actionLoading === 'sync' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    Sync Invoice Statuses
                  </Button>

                  <Button
                    variant="outline"
                    className="text-error-600 border-error-200 hover:bg-error-50"
                    onClick={handleDisconnect}
                    disabled={actionLoading === 'disconnect'}
                  >
                    {actionLoading === 'disconnect' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Unlink className="h-4 w-4" />
                    )}
                    Disconnect
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                  <p className="text-sm text-warning-700">
                    Xero is not connected. Subscriptions will still activate when purchased, but invoices will not be generated automatically.
                  </p>
                </div>

                <Button
                  variant="primary"
                  onClick={handleConnect}
                  disabled={actionLoading === 'connect'}
                >
                  {actionLoading === 'connect' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Link2 className="h-4 w-4" />
                  )}
                  Connect to Xero
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-blue-900 mb-2">How it works</h3>
          <ul className="space-y-1.5 text-sm text-blue-700 list-disc list-inside">
            <li>When an org admin purchases modules or adds seats, a Xero invoice is generated automatically</li>
            <li>The invoice is emailed to the organisation's billing contact</li>
            <li>Invoice payment statuses are synced daily from Xero</li>
            <li>Subscriptions activate immediately — payment terms are NET14 by default</li>
            <li>If Xero is disconnected, subscriptions still activate but no invoice is generated</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default XeroAdmin;
